import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { TextArea } from "@/components/Textarea";
import {
  useCreateMenuMutation,
  useUpdateMenuMutation,
} from "@/redux/services/menu/menu.api";
import { useAppSelector } from "@/redux/features/hooks";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  MenuFormData,
  UpdateMenuFormData,
} from "@/redux/services/menu/menu.type";
import { toastApiError } from "@/lib/apiErrors";

interface MenuModalProps {
  isOpen: boolean;
  onClose: () => void;
  menu?: Menu;
}

const MenuFormModal: React.FC<MenuModalProps> = ({ isOpen, onClose, menu }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [titleError, setTitleError] = useState<string | undefined>(undefined);
  const [createMenu, { isLoading: isCreating }] = useCreateMenuMutation();
  const [updateMenu, { isLoading: isUpdating }] = useUpdateMenuMutation();
  const navigate = useNavigate();
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  const isEditing = !!menu;
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    setTitleError(undefined);

    if (menu) {
      setTitle(menu.title);
      setDescription(menu.description || "");
    } else {
      setTitle("");
      setDescription("");
    }
  }, [menu, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setTitleError("Le titre du menu est requis");
      return;
    }

    try {
      if (isEditing && menu) {
        const updateData: UpdateMenuFormData = {
          title,
          description,
        };

        await updateMenu({
          id: menu.id,
          data: JSON.stringify(updateData),
        }).unwrap();

        toast.success("Menu mis à jour avec succès !");
        onClose();
      } else {
        const menuData: MenuFormData = {
          title,
          description,
          restaurant: `/api/restaurants/${restaurant?.id}`,
        };

        const result = await createMenu(menuData).unwrap();

        toast.success("Menu créé avec succès !");
        onClose();

        navigate(`/menus/${result.id}`);
      }
    } catch (error: any) {
      toastApiError(
        error,
        `Une erreur est survenue lors de ${
          isEditing ? "la modification" : "la création"
        } du menu`
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Modifier le menu" : "Créer un nouveau menu"}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div>
              <Input
                label="Titre du menu"
                type="text"
                id="title"
                placeholder="Titre du menu"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                error={titleError}
              />
            </div>

            <div>
              <TextArea
                label="Description"
                placeholder="Description du menu"
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              isLoading={isLoading}
            >
              {isEditing ? "Modifier le menu" : "Créer le menu"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MenuFormModal;
